import { GlobeAltIcon, PlusIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch } from "hooks/hooks";
import { FC, useState } from "react";
import { Helmet } from "react-helmet";
import { SubmitHandler } from "react-hook-form";
import { createContactRequest } from "redux/features/contact/contactSlice";

import TestimonialCards from "components/Testimonial";
import { CONTACT_FORM_FAQ, CONTACT_FORM_INFORMATIONS } from "data/constants";
import { ContactFormData } from "data/schemas";
import toast from "react-hot-toast";
import SocialsList from "shared/SocialsList/SocialsList";
import { socket } from "../../utils/socket";
import ContactForm from "./ContactForm";

export interface PageContactProps {
  className?: string;
}

const PageContact: FC<PageContactProps> = ({ className = "" }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const dispatch = useAppDispatch();

  const toggleFAQ = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const answerVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    try {
      const res = await dispatch(createContactRequest(data)).unwrap();
      if (res.status === "ok") {
        toast.success(res.message);
        socket.emit("send-notification", {
          action: "send_contact_request",
          email: data.email,
        });
      }
    } catch (error: any) {
      console.error("Submission error:", error);
      toast.error(error.message || "Échec de l'envoi du message.");
    }
  };

  return (
    <div className={`nc-PageContact overflow-hidden ${className}`} data-nc-id="PageContact">
      <Helmet>
        <title>Espaces à louer à Paris, Ile de France et partout en France | Event To Bee</title>
        <meta
          name="description"
          content="Vous cherchez ou avez des espaces à louer ? Contactez-nous pour en discuter ! Réponse rapide garantie !"
        />
      </Helmet>
      <div className="py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="container px-4 mx-auto max-w-7xl">
          <motion.h2 className="mb-12 text-xl font-bold text-center text-gray-900 md:text-4xl dark:text-white">
            Contactez-nous pour toute demande concernant nos espaces événementiels à louer
          </motion.h2>

          <motion.div className="mb-12 text-center">
            <p className="mt-4 text-gray-600 dark:text-gray-400 text-start">
              Une question sur une réservation ? Besoin d’un renseignement sur un espace événementiel ? Nous sommes
              là pour répondre à vos questions sur les espaces à louer à Paris, Île-de-France et partout en France.
              Que vous soyez locataire en quête du lieu idéal pour un événement ou propriétaire souhaitant mettre
              un espace en location, notre équipe vous accompagne.
            </p>
            <p className="mt-4 text-gray-600 dark:text-gray-400 text-start">
              Réponse sous <span className="font-bold text-blue-600 dark:text-blue-400">24h</span> garantie ! Nous
              nous engageons à vous répondre rapidement pour faciliter votre recherche ou la gestion de votre
              annonce. .
            </p>
          </motion.div>

          <h3 className="text-xl font-bold text-center text-gray-800 dark:text-gray-200">
            Comment nous contacter ?
          </h3>
          <div className="grid grid-cols-1 gap-12 mt-12 lg:grid-cols-2">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="space-y-8"
            >
              {CONTACT_FORM_INFORMATIONS.map((item, index) => (
                <div key={index}>
                  <h3 className="text-sm font-semibold tracking-wider uppercase dark:text-neutral-200">
                    <span className="flex-shrink-0 text-blue-600 dark:text-blue-400">{item.icon}</span>
                    {item.title}
                  </h3>
                  <span className="block mt-2 text-neutral-500 dark:text-neutral-400">{item.desc}</span>
                </div>
              ))}
              <div>
                <h4 className="flex items-center text-sm font-semibold text-gray-800 uppercase dark:text-gray-200">
                  <GlobeAltIcon className="inline-block w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                  Réseaux sociaux
                </h4>
                <SocialsList className="flex gap-4 mt-4" />
              </div>
            </motion.div>

            {/* Contact Form */}
            <ContactForm onSubmit={onSubmit} />
          </div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-16"
          >
            <h3 className="text-xl font-bold text-center text-gray-800 dark:text-gray-200">
              Espaces à louer : FAQ
            </h3>
            <div className="mt-12 space-y-4">
              {CONTACT_FORM_FAQ.map((faq, index) => (
                <div
                  key={index}
                  className="overflow-hidden border border-gray-200 rounded-lg dark:border-gray-700"
                >
                  <button
                    className="flex items-center justify-between w-full p-4 text-left transition-colors bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => toggleFAQ(index)}
                  >
                    <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">{faq.question}</h4>
                    <motion.div
                      animate={{ rotate: expandedIndex === index ? 45 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center justify-center w-8 h-8 text-white rounded-full bg-eventToBee"
                    >
                      <PlusIcon className="w-5 h-5" />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {expandedIndex === index && (
                      <motion.div
                        layout
                        variants={answerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="p-4 overflow-hidden text-gray-600 bg-white dark:text-gray-400 dark:bg-gray-900"
                      >
                        <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
        <div className="">
          <TestimonialCards title="Avis clients" />
        </div>
        <div className="container px-4 mx-auto max-w-7xl">
          <h3 className="mt-5 text-xl font-bold text-center text-gray-800 dark:text-gray-200">
            Vous cherchez l’espace idéal pour votre prochain événement ?
          </h3>
          <p className="mt-12">
            Vous avez un espace à louer ? Vous avez besoin d’un lieu pour organiser votre événement ?
          </p>
          <p>Contactez-nous dès maintenant et trouvons ensemble le lieu parfait !</p>
        </div>
      </div>
    </div>
  );
};

export default PageContact;
